import type { ReactNode } from 'react';
import { Box, Group, Stack, Text, Title } from '@mantine/core';
import { PageContainer } from './PageContainer';
import classes from './PageLayout.module.css';

export function PageHeader({
  title,
  subtitle,
  actions,
  status,
  breadcrumbs,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  status?: ReactNode;
  breadcrumbs?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" gap="md" mb={0} data-testid="page-header">
      <Box style={{ minWidth: 0, flex: '1 1 auto' }}>
        {breadcrumbs ? <Box mb={6}>{breadcrumbs}</Box> : null}
        <Group gap="sm" align="center" wrap="wrap">
          <Title order={1}>{title}</Title>
          {status}
        </Group>
        {subtitle ? (
          <Text c="dimmed" mt={6} size="sm">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Group gap="sm" wrap="wrap" justify="flex-end" data-testid="page-header-actions">
          {actions}
        </Group>
      ) : null}
    </Group>
  );
}

/**
 * @param layout `scroll` — page scrolls in AppShell mainInner (default).
 * `workspace` — fill viewport; only nested tables/panels scroll (mainInner overflow hidden via :has).
 */
export function PageShell({
  children,
  layout = 'scroll',
}: {
  children: ReactNode;
  layout?: 'scroll' | 'workspace';
}) {
  return (
    <PageContainer>
      <Stack gap="md" className={layout === 'workspace' ? classes.workspace : classes.scroll} data-layout={layout}>
        {children}
      </Stack>
    </PageContainer>
  );
}

export function PageSection({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap="sm" component="section">
      {title || actions ? (
        <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Box>
            {title ? (
              <Title order={2} size="h4">
                {title}
              </Title>
            ) : null}
            {description ? (
              <Text c="dimmed" size="sm" mt={4}>
                {description}
              </Text>
            ) : null}
          </Box>
          {actions}
        </Group>
      ) : null}
      {children}
    </Stack>
  );
}

export function Toolbar({ children }: { children: ReactNode }) {
  return (
    <Group gap="sm" wrap="wrap" data-testid="toolbar">
      {children}
    </Group>
  );
}

export function PageActions({ children }: { children: ReactNode }) {
  return (
    <Group gap="sm" wrap="wrap" justify="flex-end" data-testid="page-actions">
      {children}
    </Group>
  );
}
